import React, { useContext, useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMail, FiMapPin, FiArrowRight, FiMessageCircle, FiClock } from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import { SUPPORT_EMAIL } from '../config/emailjs';
import ScrollVelocity from './ScrollVelocity';

const productLinks = [
  { to: '/funcionalidades', label: 'Funcionalidades' },
  { to: '/planes', label: 'Planes' },
  { to: '/ayuda', label: 'Ayuda' },
];

const companyLinks = [
  { to: '/quienes-somos', label: 'Quiénes somos' },
];

const legalLinks = [
  { to: '/terminos', label: 'Términos de uso' },
  { to: '/privacidad', label: 'Privacidad' },
];

const Footer = () => {
  const { user } = useContext(AuthContext);
  const { pathname } = useLocation();
  const scrollContainerRef = useRef(null);
  const hideCta = ['/', '/register', '/login', '/planes', '/contacto', '/contactos'].includes(pathname);

  useLayoutEffect(() => {
    scrollContainerRef.current = document.getElementById('main-scroll');
  }, [pathname]);

  const openContact = () => {
    window.dispatchEvent(new CustomEvent('openContactModal'));
  };

  return (
    <footer className="bg-black text-white">
      {!hideCta && (
        <div className="border-b border-white/10 bg-[#004AAD]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/90 text-sm font-medium text-center sm:text-left">
              ¿Listo para ordenar tu negocio?
            </p>
            <div className="flex gap-2">
              {user ? (
                <Link
                  to="/inicio"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white text-[#004AAD] text-sm font-semibold hover:bg-[#5CA3FF] hover:text-white transition-colors"
                >
                  Ir al panel
                  <FiArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white text-[#004AAD] text-sm font-semibold hover:bg-[#5CA3FF] hover:text-white transition-colors"
                  >
                    Empezar gratis
                    <FiArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/planes"
                    className="px-3.5 py-2 rounded-lg border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
                  >
                    Ver planes
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10">
          <div className="sm:col-span-2 lg:col-span-1">
            <img
              src="/logowhite.png"
              alt="Contsuite"
              className="h-8 w-auto max-w-[11rem] object-contain object-left mb-4 block"
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/logo-espanolwhite.png';
              }}
            />
            <p className="text-sm text-gray-400 max-w-xs leading-relaxed">
              Cobranzas para PyMEs: prioridad de cobro, cierre de mes y pack al contador. Registro interno sin facturación ARCA.
            </p>
          </div>

          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-white/70 mb-3">Producto</h3>
            <ul className="space-y-2 text-sm">
              {productLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-gray-400 hover:text-white transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
              {!user && (
                <>
                  <li>
                    <Link to="/login" className="text-gray-400 hover:text-white transition-colors">Ingresar</Link>
                  </li>
                  <li>
                    <Link to="/register" className="text-gray-400 hover:text-white transition-colors">Crear cuenta</Link>
                  </li>
                </>
              )}
            </ul>
          </div>

          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-white/70 mb-3">Empresa</h3>
            <ul className="space-y-2 text-sm">
              {companyLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-gray-400 hover:text-white transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <button type="button" onClick={openContact} className="text-gray-400 hover:text-white transition-colors">
                  Contacto
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-white/70 mb-3">Legal</h3>
            <ul className="space-y-2 text-sm">
              {legalLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-gray-400 hover:text-white transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-white/70 mb-3">Contacto</h3>
            <ul className="space-y-2.5 text-sm text-gray-400">
              <li>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-2 hover:text-white transition-colors">
                  <FiMail className="w-4 h-4 shrink-0" />
                  {SUPPORT_EMAIL}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <FiMapPin className="w-4 h-4 shrink-0" />
                CABA, Argentina
              </li>
              <li className="flex items-center gap-2">
                <FiClock className="w-4 h-4 shrink-0" />
                Respuesta en 24 h hábiles
              </li>
            </ul>
            <button
              type="button"
              onClick={openContact}
              className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 text-sm text-white hover:bg-white/10 transition-colors"
            >
              <FiMessageCircle className="w-4 h-4" />
              Escribinos
            </button>
          </div>
        </div>

        <div className="border-t border-white/10 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-gray-500 text-xs">
            © {new Date().getFullYear()} Contsuite. Todos los derechos reservados.
          </p>
          <p className="text-gray-500 text-xs">Hecho en Argentina</p>
        </div>
      </div>

      <div className="border-t border-white/10 py-3 md:py-4 overflow-hidden" aria-hidden="true">
        <ScrollVelocity
          scrollContainerRef={scrollContainerRef}
          texts={['Contsuite', 'Contsuite']}
          velocity={100}
          className="custom-scroll-text"
          numCopies={6}
          damping={50}
          stiffness={400}
        />
      </div>
    </footer>
  );
};

export default Footer;
